import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import OTPVerificationModal from '../auth/OTPVerificationModal';
import axiosInstance from '../../config/axios.config';

const AdminDetailsStep = ({
    formData,
    updateFormData,
    errors,
    emailVerified,
    onEmailVerified,
    toast
}) => {
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);

    // Send OTP
    const handleSendOTP = async () => {
        if (!formData.email) {
            toast.current.show({
                severity: 'warn',
                summary: 'Email Required',
                detail: 'Please enter your email address first',
                life: 3000
            });
            return;
        }

        setSendingOtp(true);

        try {
            const response = await axiosInstance.post('/auth/send-otp', {
                email: formData.email
            });

            if (response.data.success) {
                setShowOtpModal(true);
                toast.current.show({
                    severity: 'success',
                    summary: 'OTP Sent',
                    detail: 'Please check your email for the verification code',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to send OTP',
                life: 5000
            });
        } finally {
            setSendingOtp(false);
        }
    };

    // OTP verified callback
    const handleOtpVerified = (otp) => {
        setShowOtpModal(false);
        onEmailVerified(otp);
    };

    // Resend OTP
    const handleOtpResend = async () => {
        return handleSendOTP();
    };

    // Password strength indicator
    const passwordHeader = <div className="font-bold mb-3">Pick a password</div>;
    const passwordFooter = (
        <>
            <Divider />
            <p className="mt-2">Suggestions:</p>
            <ul className="pl-2 ml-2 mt-0 line-height-3">
                <li>At least one lowercase</li>
                <li>At least one uppercase</li>
                <li>At least one numeric</li>
                <li>Minimum 8 characters</li>
            </ul>
        </>
    );

    return (
        <div className="p-fluid">
            {/* Personal Information Section */}
            <div className="section-header mb-3">
                <h4 className="m-0 text-900 text-base">
                    <i className="pi pi-user mr-2"></i>
                    Owner / Admin Details
                </h4>
                <p className="mt-1 mb-0 text-600 text-sm">Personal information and login credentials for company administrator</p>
            </div>

            <div className="formgrid grid">
                {/* Name Fields Row */}
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="first_name" className="font-semibold text-900 text-sm">
                            <i className="pi pi-user mr-2"></i>
                            First Name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="first_name"
                            name="first_name"
                            value={formData.first_name || ''}
                            onChange={(e) => updateFormData('first_name', e.target.value)}
                            placeholder="Enter your first name"
                            className={classNames('w-full', { 'p-invalid': errors.first_name })}
                        />
                        {errors.first_name && <small className="p-error">{errors.first_name}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="last_name" className="font-semibold text-900 text-sm">
                            <i className="pi pi-user mr-2"></i>
                            Last Name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="last_name"
                            name="last_name"
                            value={formData.last_name || ''}
                            onChange={(e) => updateFormData('last_name', e.target.value)}
                            placeholder="Enter your last name"
                            className={classNames('w-full', { 'p-invalid': errors.last_name })}
                        />
                        {errors.last_name && <small className="p-error">{errors.last_name}</small>}
                    </div>
                </div>

                {/* Email Field with Verification */}
                <div className="col-12">
                    <div className="field mb-3">
                        <label htmlFor="email" className="font-semibold text-900 text-sm">
                            <i className="pi pi-envelope mr-2"></i>
                            Email Address <span className="text-red-500">*</span>
                            {emailVerified && <span className="text-green-500 ml-2">✓ Verified</span>}
                        </label>
                        <div className="p-inputgroup">
                            <InputText
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email || ''}
                                onChange={(e) => {
                                    updateFormData('email', e.target.value);
                                }}
                                placeholder="Enter your email address"
                                className={classNames('', { 'p-invalid': errors.email })}
                                disabled={emailVerified}
                            />
                            <Button
                                type="button"
                                label={emailVerified ? "Verified ✓" : "Verify Email"}
                                icon={emailVerified ? "pi pi-check" : "pi pi-envelope"}
                                onClick={handleSendOTP}
                                disabled={emailVerified || !formData.email || sendingOtp}
                                loading={sendingOtp}
                                className={emailVerified ? "p-button-success" : ""}
                                style={{ minWidth: '130px' }}
                            />
                        </div>
                        {errors.email && <small className="p-error">{errors.email}</small>}
                        {!emailVerified && formData.email && (
                            <small className="text-600 mt-1 block">Click "Verify Email" to receive OTP</small>
                        )}
                    </div>
                </div>

                {/* Mobile Number Field */}
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="mobile_number" className="font-semibold text-900 text-sm">
                            <i className="pi pi-phone mr-2"></i>
                            Mobile Number <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="mobile_number"
                            name="mobile_number"
                            type="tel"
                            value={formData.mobile_number || ''}
                            onChange={(e) => updateFormData('mobile_number', e.target.value)}
                            placeholder="Enter 10-digit mobile number"
                            className={classNames('w-full', { 'p-invalid': errors.mobile_number })}
                            maxLength={10}
                            keyfilter="int"
                        />
                        {errors.mobile_number && <small className="p-error">{errors.mobile_number}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    {/* Empty column for alignment */}
                </div>

                {/* Password Fields */}
                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="password" className="font-semibold text-900 text-sm">
                            <i className="pi pi-lock mr-2"></i>
                            Password <span className="text-red-500">*</span>
                        </label>
                        <Password
                            id="password"
                            name="password"
                            value={formData.password || ''}
                            onChange={(e) => updateFormData('password', e.target.value)}
                            placeholder="Enter password"
                            toggleMask
                            className={classNames('w-full', { 'p-invalid': errors.password })}
                            inputClassName="w-full"
                            header={passwordHeader}
                            footer={passwordFooter}
                        />
                        {errors.password && <small className="p-error">{errors.password}</small>}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mb-3">
                        <label htmlFor="password_confirmation" className="font-semibold text-900 text-sm">
                            <i className="pi pi-lock mr-2"></i>
                            Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <Password
                            id="password_confirmation"
                            name="password_confirmation"
                            value={formData.password_confirmation || ''}
                            onChange={(e) => updateFormData('password_confirmation', e.target.value)}
                            placeholder="Re-enter password"
                            toggleMask
                            className={classNames('w-full', {
                                'p-invalid': errors.password_confirmation ||
                                    (formData.password && formData.password_confirmation && formData.password !== formData.password_confirmation)
                            })}
                            inputClassName="w-full"
                            feedback={false}
                        />
                        {errors.password_confirmation && <small className="p-error">{errors.password_confirmation}</small>}
                    </div>
                </div>

            </div>

            {/* OTP Verification Modal */}
            <OTPVerificationModal
                visible={showOtpModal}
                onHide={() => setShowOtpModal(false)}
                email={formData.email}
                onVerified={handleOtpVerified}
                onResend={handleOtpResend}
            />
        </div>
    );
};

export default AdminDetailsStep;
